import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Partnerdetail.css";
import "./styles/HewlettPackardEnterprise.css";
import hpeLogo from "../../assets/images/partners/HewlettPackardEnterprise.png";

function useCounter(target, duration = 2000, inView) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, inView]);
  return count;
}
function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const archLayers = [
  { label: "MGMT", nodes: [{ t: "HPE GreenLake Console", c: "green" }, { t: "OneView", c: "green" }] },
  { label: "COMPUTE", nodes: [{ t: "ProLiant Gen11", c: "green" }, { t: "Synergy", c: "green" }, { t: "Apollo HPC", c: "gray" }] },
  { label: "STORAGE", nodes: [{ t: "Primera / Alletra", c: "green" }, { t: "Nimble", c: "gray" }, { t: "StoreOnce", c: "gray" }] },
  { label: "NETWORK", nodes: [{ t: "Aruba CX", c: "green" }, { t: "Aruba Central", c: "gray" }] },
  { label: "EDGE", nodes: [{ t: "Edgeline EL300", c: "green" }, { t: "SimpliVity", c: "gray" }] },
];

const greenlakeBenefits = [
  { title: "Consumption-Based Pricing", desc: "Pay only for what you use with metered billing — no upfront capital commitment, no over-provisioning waste." },
  { title: "On-Premises Cloud Experience", desc: "Cloud operating model with on-premises data sovereignty — full control with cloud-like agility." },
  { title: "HPE Managed or Self-Managed", desc: "Choose between HPE managing the infrastructure or self-managing through the unified GreenLake console." },
  { title: "Multi-Cloud Integration", desc: "GreenLake connects to Azure, AWS, and GCP — extending on-premises into a true hybrid multi-cloud." },
];

const computeCards = [
  {
    cat: "Rack Servers", name: "HPE ProLiant Gen11 Servers", img: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=900&q=80",
    desc: "The world's most secure industry-standard servers — built with Silicon Root of Trust security, Intel Xeon Scalable and AMD EPYC processor support, and iLO 6 management for intelligent lifecycle automation.",
    specs: [["CPU Support", "Intel Xeon / AMD EPYC"], ["GPU Support", "Up to 8x H100/A100"], ["RAM Capacity", "Up to 24TB DIMM"], ["Management", "iLO 6 + OneView"]],
  },
  {
    cat: "Composable Infra", name: "HPE Synergy Composable", img: "https://images.unsplash.com/photo-1597852074816-d933c7d2b988?auto=format&fit=crop&w=900&q=80",
    desc: "HPE Synergy is the world's first composable infrastructure platform — software-defined compute, storage, and fabric dynamically composed for any workload through a single management interface.",
    specs: [["Frame Form Factor", "12U Blade Frame"], ["Connectivity", "40/100GbE Fabric"], ["Storage Zones", "D3940 Direct-Attach"], ["Automation", "OneView API-Driven"]],
  },
  {
    cat: "HPC Platforms", name: "HPE Apollo & Cray Supercomputing", img: "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=900&q=80",
    desc: "HPE Cray EX supercomputers and Apollo HPC platforms deliver petaflop-scale computing for AI training, climate modeling, genomic sequencing, and engineering simulation workloads.",
    specs: [["Peak Performance", "Petaflop+ Scale"], ["Cooling", "Direct Liquid Cooling"], ["Interconnect", "Slingshot High-Speed"], ["AI Frameworks", "NVIDIA CUDA / ROCm"]],
  },
];

const storageOptions = [
  { icon: "⚡", name: "HPE Alletra Storage MP", desc: "HPE's most advanced all-flash storage platform — with sub-100μs latency, 100% data availability guarantee, and cloud-connected analytics for predictive support." },
  { icon: "💾", name: "HPE Primera A-Series", desc: "Tier-1 all-flash storage delivering six-nines availability for mission-critical Oracle, SAP, and Tier-1 application workloads requiring guaranteed performance." },
  { icon: "📦", name: "HPE Nimble Storage", desc: "Secondary all-flash and hybrid flash storage with InfoSight AI-powered analytics predicting 86% of issues before they cause impact — the most proactive storage platform available." },
  { icon: "🔄", name: "HPE StoreOnce Backup", desc: "Purpose-built backup appliances with StoreOnce Catalyst deduplication delivering up to 65:1 reduction ratios — integrated with Veeam, Commvault, and Veritas." },
];

const hpcCards = [
  { icon: "🤖", name: "HPE AI Factory", desc: "Validated AI infrastructure reference architectures for NVIDIA GPU clusters — supporting Hopper and Blackwell architectures for large language model training.", tags: ["NVIDIA H100/H200", "GPU Clustering", "InfiniBand Network"] },
  { icon: "🧬", name: "Life Sciences HPC", desc: "HPE Cray platforms for genomics, drug discovery, and molecular dynamics — used by leading research institutions and pharmaceutical companies worldwide.", tags: ["GROMACS", "AMBER", "Alphafold Ready"] },
  { icon: "🌡️", name: "Climate & Scientific", desc: "National weather prediction, climate modeling, and scientific simulation at exascale using HPE Cray EX with liquid-cooled HPE-designed cabinets.", tags: ["OpenFOAM", "WRF Weather", "CESM Climate"] },
  { icon: "🏭", name: "Manufacturing Simulation", desc: "CFD, FEA, and digital twin simulations running on HPE Apollo dense compute nodes with NVIDIA RTX GPU visualization clusters.", tags: ["ANSYS", "STAR-CCM+", "Siemens NX"] },
  { icon: "🔐", name: "HPE AI Privacy Guard", desc: "Confidential computing capabilities for AI training on sensitive data — using AMD SEV-SNP and Intel TDX hardware security to protect training datasets.", tags: ["Confidential AI", "AMD SEV-SNP", "Intel TDX"] },
  { icon: "📊", name: "HPE Ezmeral Data Fabric", desc: "Enterprise data platform unifying data access across edge, on-premises, and cloud — supporting AI/ML pipelines with consistent APIs regardless of data location.", tags: ["Data Mesh", "Multi-Cloud", "S3 Compatible"] },
];

const edgePoints = [
  { num: "01", title: "HPE Edgeline Converged Edge Systems", desc: "Rugged 1U edge servers with industrial temperature ratings, wide DC power input, and shock/vibration resistance — deploying enterprise compute to factory floors, substations, and remote locations." },
  { num: "02", title: "Aruba Edge Connect SD-WAN", desc: "Software-defined WAN extending connectivity from data center to hundreds of edge locations — with AI-powered traffic optimization and built-in security for distributed enterprises." },
  { num: "03", title: "HPE SimpliVity HCI at the Edge", desc: "Hyperconverged infrastructure in a compact 2U form factor for remote office/branch office deployments — full enterprise compute, storage, and backup in a box managed from a central vCenter." },
  { num: "04", title: "Aruba Central for Edge Management", desc: "Cloud-native network management platform providing unified visibility and control over thousands of Aruba access points, switches, and gateways from a single console." },
];

const sustMetrics = [
  { val: "100%", label: "Renewable Electricity" },
  { val: "50%", label: "Emissions Reduction" },
  { val: "2030", label: "Net Zero Target" },
];

const industries = [
  { icon: "🏥", name: "Healthcare", desc: "HPE GreenLake for healthcare data — HIPAA-compliant HPE infrastructure running EHR, PACS, and genomics workloads with HPE-managed operations." },
  { icon: "🏦", name: "Financial Services", desc: "HPE Primera for low-latency trading databases, HPE Synergy for multi-tenant private cloud, and GreenLake for OpEx-model regulated financial infrastructure." },
  { icon: "🎓", name: "Research & Education", desc: "HPE Cray supercomputers for academic research, Apollo HPC for university computing clusters, and GreenLake consumption billing for flexible academic budget cycles." },
  { icon: "🏛️", name: "Government & Defense", desc: "TAA-compliant HPE hardware, IL4/IL5 GreenLake Government Cloud, and HPE's Silicon Root of Trust for supply-chain-secure government deployments." },
  { icon: "🏭", name: "Manufacturing", desc: "HPE Edgeline for shop-floor edge computing, Aruba for plant-wide wireless, and HPE Ezmeral for IoT analytics pipelines aggregating sensor data at scale." },
  { icon: "🛢️", name: "Oil & Gas / Energy", desc: "HPE rugged edge systems for remote field computing, HPE Apollo HPC for seismic data processing, and Aruba mesh networking for pipeline monitoring." },
  { icon: "📡", name: "Telecommunications", desc: "HPE OpenRAN infrastructure for 5G network deployments, HPE NFV Director for virtual network function orchestration, and Cray for telco AI workloads." },
  { icon: "🚀", name: "Aerospace & Defense", desc: "HPE's ITAR-registered server manufacturing, classified HPC clusters for simulation, and tamper-resistant ProLiant servers for defense environments." },
];

const lifecycleSteps = [
  { icon: "🔍", title: "Assess", desc: "Infrastructure discovery, workload profiling, and GreenLake consumption modeling." },
  { icon: "📐", title: "Design", desc: "Solution architecture aligned to workload, performance, and availability requirements." },
  { icon: "🏗️", title: "Deploy", desc: "HPE factory integration and certified engineer on-site installation and cabling." },
  { icon: "✅", title: "Validate", desc: "Performance testing, failover validation, and management integration verification." },
  { icon: "⚙️", title: "Operate", desc: "24/7 monitoring, firmware management, and GreenLake performance reporting." },
];

const certs = [
  { icon: "🏆", name: "HPE Gold Partner", desc: "Gold-tier authorized HPE partner across Compute, Storage, and Networking" },
  { icon: "🖥️", name: "HPE ProLiant Certified", desc: "Server sales and deployment specialist across the complete ProLiant Gen11 portfolio" },
  { icon: "💾", name: "HPE Alletra Storage", desc: "Authorized storage partner for HPE Alletra, Primera, and Nimble platforms" },
  { icon: "☁️", name: "HPE GreenLake Partner", desc: "Authorized HPE GreenLake as-a-service delivery partner for managed deployments" },
];

const faqData = [
  { q: "What is HPE GreenLake and how does it work?", a: "HPE GreenLake is HPE's cloud services platform that delivers on-premises IT infrastructure as a metered, consumption-based service — similar to cloud billing but running on your own floor or HPE co-location. Organizations choose GreenLake to avoid capital expenditure while maintaining on-premises data sovereignty, compliance, and performance. HPE installs hardware at your location, manages it proactively through the GreenLake Central console, and bills monthly based on actual resource consumption (compute cores, storage TiB, VMs). GreenLake spans compute (ProLiant, Synergy), storage (Alletra, Primera, Nimble), HCI (SimpliVity, Nutanix on HPE), networking, HPC, and data services. ALG Data Guard acts as your HPE GreenLake delivery partner — managing the solution from design through operations." },
  { q: "What is the difference between HPE ProLiant and HPE Synergy?", a: "HPE ProLiant servers are traditional rack-mount and tower servers optimized for specific workloads — individual servers you configure, deploy, and manage independently. ProLiant Gen11 spans from the DL20 Gen11 for remote offices to the DL580 Gen11 for in-memory databases and the XL series for high-density HPC. HPE Synergy is HPE's composable infrastructure platform in a blade form factor — it's not just servers, it's a composable pool of compute, storage, and fabric resources that can be dynamically allocated to workloads through software. Synergy is best for organizations running mixed private cloud workloads that need to allocate and reallocate infrastructure resources rapidly." },
  { q: "What workloads is HPE Apollo and Cray designed for?", a: "HPE Apollo systems target high-performance computing (HPC) and AI workloads requiring dense compute and high-speed interconnect — where standard rack servers can't deliver the per-rack performance needed. The Apollo 6500 platform, for example, supports 8 NVIDIA H100 or AMD MI300X GPUs per node with NVLink GPU interconnect and 400Gb/s InfiniBand networking between nodes — enabling AI training runs that would be impossible on standard compute. HPE Cray EX supercomputers are deployed at national laboratories and research institutions for petaflop to exaflop simulation workloads. ALG Data Guard helps organizations assess whether HPC or GPU-dense Apollo infrastructure is appropriate for their AI workloads — many enterprises find that 2–4 Apollo nodes deliver the AI training performance they need without full HPC infrastructure." },
  { q: "What is HPE Alletra Storage and how does it compare to HPE Primera and Nimble?", a: "HPE Alletra Storage MP is HPE's current flagship unified all-flash storage platform, replacing Primera as the premium tier for mission-critical workloads. Alletra MP delivers sub-100μs latency, block/file/object protocols from a single platform, cloud-connected management through HPE Storage Ops Console, and HPE's 100% data availability guarantee. HPE Primera (now reaching end of new sales) was the predecessor tier-1 platform — excellent for Oracle and SAP HANA with six-nines availability. HPE Nimble is the mid-range platform using a capacity-optimized architecture with InfoSight AI — best for secondary workloads, VDI, file services, and backup. ALG Data Guard helps you select the right HPE storage tier based on application performance requirements, capacity growth projections, and budget." },
  { q: "How does ALG Data Guard support HPE environments after deployment?", a: "ALG Data Guard provides comprehensive HPE managed services: HPE OneView health monitoring and proactive hardware alerting, iLO firmware and driver lifecycle management, GreenLake performance reporting and consumption optimization, HPE storage capacity planning and performance analysis through InfoSight and Storage Ops Console, Aruba network monitoring through Aruba Central, HPE SimpliVity backup policy management, and quarterly infrastructure health reviews. For organizations on HPE GreenLake, we act as the managed service layer handling day-to-day operations while HPE manages the hardware lifecycle and consumption billing." },
];

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
);
const ChevronDown = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
);

export default function HewlettPackardEnterprise() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [metricsRef, metricsInView] = useInView(0.3);
  const c1 = useCounter(160, 2000, metricsInView);
  const c2 = useCounter(100, 2000, metricsInView);
  const c3 = useCounter(90, 2000, metricsInView);
  const c4 = useCounter(600, 2200, metricsInView);

  return (
    <div className="hpe-page pd-page">

      {/* ── Identity Band ── */}
      <div className="pd-identity-band">
        <div className="pd-identity-inner">
          <img src={hpeLogo} alt="Hewlett Packard Enterprise" className="pd-partner-logo" />
          <div className="pd-identity-divider" aria-hidden="true" />
          <div className="pd-identity-text">
            <div className="pd-eyebrow pd-eyebrow--dark">
              <span className="pd-live-dot pd-live-dot--dark" />
              Enterprise Infrastructure & Cloud Partner
            </div>
            <h1 className="pd-identity-name">ALG Data Guard × Hewlett Packard Enterprise</h1>
            <p className="pd-identity-desc">
              Hewlett Packard Enterprise is a global leader in enterprise servers, storage, networking, and hybrid cloud — delivering HPE GreenLake as-a-service, ProLiant and Cray HPC systems, Alletra all-flash storage, and Aruba networking. ALG Data Guard is an HPE Gold Certified Partner providing design, procurement, deployment, and managed services across the full HPE portfolio.
            </p>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="hpe-hero">
        <div className="hpe-hero__top-stripe" aria-hidden="true" />
        <div className="hpe-hero__bg-pattern" aria-hidden="true" />
        <div className="hpe-hero__glow" aria-hidden="true" />
        <div className="hpe-hero__inner">
          <div>
            <div className="hpe-hero__eyebrow">
              <span className="hpe-hero__eyebrow-dot" />
              HPE Gold Certified Partner — Infrastructure & GreenLake
            </div>
            <h2 className="hpe-hero__title">
              Enterprise Infrastructure.<br />Built for <em>Hybrid Cloud</em>.
            </h2>
            <p className="hpe-hero__body">
              From petaflop-scale HPC clusters to as-a-service edge deployments — HPE delivers the hardware and management platform that powers the world's most demanding enterprise and scientific workloads. ALG Data Guard brings certified HPE expertise to your infrastructure challenges.
            </p>
            <div className="hpe-hero__actions">
              <button className="hpe-btn hpe-btn--green" onClick={() => navigate("/contact")}>
                Request an HPE Assessment <ArrowIcon />
              </button>
              <button className="hpe-btn hpe-btn--ghost-w" onClick={() => navigate("/contact")}>
                Explore HPE Portfolio
              </button>
            </div>
            <div className="hpe-hero__proof">
              <div>
                <div className="hpe-hero__proof-val">GreenLake</div>
                <div className="hpe-hero__proof-label">As-a-Service Platform</div>
              </div>
              <div>
                <div className="hpe-hero__proof-val">Gold</div>
                <div className="hpe-hero__proof-label">ALG Data Guard Partner Tier</div>
              </div>
              <div>
                <div className="hpe-hero__proof-val">HPC</div>
                <div className="hpe-hero__proof-label">Cray Supercomputing</div>
              </div>
            </div>
          </div>

          <div className="hpe-arch-card">
            <div className="hpe-arch-card__header">
              <span className="hpe-arch-card__header-dot" />
              HPE INFRASTRUCTURE STACK — GREENLAKE MANAGED
            </div>
            {archLayers.map(layer => (
              <div key={layer.label} className="hpe-arch-layer">
                <span className="hpe-arch-layer__label">{layer.label}</span>
                <div className="hpe-arch-layer__nodes">
                  {layer.nodes.map(n => (
                    <span key={n.t} className={`hpe-arch-node hpe-arch-node--${n.c}`}>{n.t}</span>
                  ))}
                </div>
              </div>
            ))}
            <div className="hpe-arch-card__footer">▶ GreenLake Central — Unified Management Console</div>
          </div>
        </div>
      </section>

      {/* ── GreenLake ── */}
      <section className="hpe-greenlake">
        <div className="hpe-shell">
          <div className="hpe-greenlake__inner">
            <img
              src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=900&q=80"
              alt="HPE GreenLake cloud platform"
              className="hpe-greenlake__img"
            />
            <div>
              <div className="hpe-tag hpe-tag--dark"><span className="hpe-blink" /> HPE GreenLake</div>
              <h2 className="hpe-greenlake__title">On-Premises Cloud. Your Way.</h2>
              <p className="hpe-greenlake__body">
                HPE GreenLake delivers the cloud experience — consumption billing, elastic capacity, and managed operations — on infrastructure that runs in your data center or co-location. Your data never leaves your premises, your performance is consistent, and your costs are predictable. ALG Data Guard designs and manages GreenLake deployments for organizations that need cloud economics without cloud compromises.
              </p>
              <div className="hpe-greenlake__benefits">
                {greenlakeBenefits.map(b => (
                  <div key={b.title} className="hpe-greenlake-benefit">
                    <div className="hpe-greenlake-benefit__title">{b.title}</div>
                    <p className="hpe-greenlake-benefit__desc">{b.desc}</p>
                  </div>
                ))}
              </div>
              <button className="hpe-btn hpe-btn--green" onClick={() => navigate("/contact")}>
                Explore GreenLake <ArrowIcon />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Compute Portfolio ── */}
      <section className="hpe-compute">
        <div className="hpe-shell">
          <div className="hpe-compute__head">
            <div className="hpe-tag hpe-tag--green"><span className="hpe-blink" /> Compute Portfolio</div>
            <h2 className="hpe-title">HPE Servers — From Edge to Exascale</h2>
            <p className="hpe-sub">HPE's compute portfolio spans every workload — from standard 1U rack servers to GPU-dense AI platforms and petaflop Cray supercomputers.</p>
          </div>
          <div className="hpe-compute__grid">
            {computeCards.map(card => (
              <div key={card.name} className="hpe-compute-card">
                <div className="hpe-compute-card__img-wrap">
                  <img src={card.img} alt={card.name} className="hpe-compute-card__img" />
                  <span className="hpe-compute-card__cat">{card.cat}</span>
                </div>
                <div className="hpe-compute-card__body">
                  <h3 className="hpe-compute-card__name">{card.name}</h3>
                  <p className="hpe-compute-card__desc">{card.desc}</p>
                  <div className="hpe-compute-card__specs">
                    {card.specs.map(([key, val]) => (
                      <div key={key} className="hpe-spec-row">
                        <span className="hpe-spec-row__key">{key}</span>
                        <span className="hpe-spec-row__val">{val}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Storage ── */}
      <section className="hpe-storage">
        <div className="hpe-shell">
          <div className="hpe-storage__inner">
            <div>
              <div className="hpe-tag hpe-tag--dark"><span className="hpe-blink" /> Storage Solutions</div>
              <h2 className="hpe-storage__title">All-Flash Storage with 100% Data Availability</h2>
              <p className="hpe-storage__body">
                HPE's storage portfolio covers every tier — from Alletra MP's tier-1 all-flash platform with sub-100μs latency and 100% data availability guarantee, to Nimble's AI-powered secondary storage, to StoreOnce purpose-built backup appliances. All managed through the unified HPE Storage Ops Console with InfoSight AI predicting and preventing issues before they occur.
              </p>
              <div className="hpe-storage__options">
                {storageOptions.map(opt => (
                  <div key={opt.name} className="hpe-storage-option">
                    <span className="hpe-storage-option__icon">{opt.icon}</span>
                    <div>
                      <div className="hpe-storage-option__name">{opt.name}</div>
                      <p className="hpe-storage-option__desc">{opt.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="hpe-btn hpe-btn--green" onClick={() => navigate("/contact")}>
                  Get a Storage Assessment <ArrowIcon />
                </button>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1597852074816-d933c7d2b988?auto=format&fit=crop&w=900&q=80"
              alt="HPE all-flash storage"
              className="hpe-storage__img"
            />
          </div>
        </div>
      </section>

      {/* ── HPC & AI ── */}
      <section className="hpe-hpc">
        <div className="hpe-shell">
          <div className="hpe-hpc__head">
            <div className="hpe-tag hpe-tag--green"><span className="hpe-blink" /> HPC & Artificial Intelligence</div>
            <h2 className="hpe-title">High-Performance Computing & AI Infrastructure</h2>
            <p className="hpe-sub" style={{ margin:"0 auto", maxWidth:"640px" }}>
              HPE is the world's leading HPC provider — deploying more of the world's top 500 supercomputers than any other manufacturer. From enterprise AI clusters to national laboratory-scale computation.
            </p>
          </div>
          <div className="hpe-hpc__grid">
            {hpcCards.map(card => (
              <div key={card.name} className="hpe-hpc-card">
                <span className="hpe-hpc-card__icon">{card.icon}</span>
                <h3 className="hpe-hpc-card__name">{card.name}</h3>
                <p className="hpe-hpc-card__desc">{card.desc}</p>
                <div className="hpe-hpc-card__tags">{card.tags.map(t => <span key={t} className="hpe-hpc-card__tag">{t}</span>)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Edge Computing ── */}
      <section className="hpe-edge">
        <div className="hpe-shell">
          <div className="hpe-edge__inner">
            <img
              src="https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=900&q=80"
              alt="HPE edge computing"
              className="hpe-edge__img"
            />
            <div>
              <div className="hpe-tag hpe-tag--green"><span className="hpe-blink" /> Edge Computing</div>
              <h2 className="hpe-title">Enterprise Intelligence at the Edge</h2>
              <p className="hpe-sub" style={{ marginBottom:"1.5rem" }}>
                HPE extends enterprise computing beyond the data center to the edge — from retail locations and manufacturing plants to oil rigs and remote military installations. Aruba provides the connectivity; HPE Edgeline provides the compute.
              </p>
              <div className="hpe-edge__points">
                {edgePoints.map(p => (
                  <div key={p.num} className="hpe-edge-point">
                    <div className="hpe-edge-point__num">{p.num}</div>
                    <div>
                      <div className="hpe-edge-point__title">{p.title}</div>
                      <p className="hpe-edge-point__desc">{p.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Sustainability ── */}
      <section className="hpe-sustainability">
        <div className="hpe-shell">
          <div className="hpe-sustainability__inner">
            <img
              src="https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?auto=format&fit=crop&w=900&q=80"
              alt="HPE sustainability and green data centers"
              className="hpe-sustainability__img"
            />
            <div>
              <div className="hpe-tag hpe-tag--dark"><span className="hpe-blink" /> Sustainability</div>
              <h2 className="hpe-sustainability__title">Engineering a Net Zero Future</h2>
              <p className="hpe-sustainability__body">
                HPE is committed to net zero greenhouse gas emissions across its value chain by 2040 — already achieving 100% renewable electricity in operations, implementing liquid cooling to reduce data center power consumption by 40%, and designing products for circular economy practices including take-back and refurbishment programs.
              </p>
              <p className="hpe-sustainability__body">
                HPE's efficiency leadership means choosing HPE infrastructure delivers both performance and sustainability credentials — with HPE's TüV SÜD-certified carbon footprint reporting for procurement and ESG reporting requirements.
              </p>
              <div className="hpe-sustainability__metrics">
                {sustMetrics.map(m => (
                  <div key={m.val} className="hpe-sust-metric">
                    <span className="hpe-sust-metric__val">{m.val}</span>
                    <span className="hpe-sust-metric__label">{m.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Industries ── */}
      <section className="hpe-industries">
        <div className="hpe-shell">
          <div className="hpe-industries__head">
            <div className="hpe-tag hpe-tag--green"><span className="hpe-blink" /> Industry Solutions</div>
            <h2 className="hpe-title">HPE Infrastructure Across Every Vertical</h2>
            <p className="hpe-sub" style={{ margin:"0 auto", maxWidth:"640px" }}>HPE's validated industry solutions span enterprise, government, research, and industrial environments with compliance-specific configurations.</p>
          </div>
          <div className="hpe-industries__grid">
            {industries.map(ind => (
              <div key={ind.name} className="hpe-industry-card">
                <span className="hpe-industry-card__icon">{ind.icon}</span>
                <h3 className="hpe-industry-card__name">{ind.name}</h3>
                <p className="hpe-industry-card__desc">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Lifecycle ── */}
      <section className="hpe-lifecycle">
        <div className="hpe-shell">
          <div className="hpe-lifecycle__head">
            <div className="hpe-tag hpe-tag--dark"><span className="hpe-blink" /> Delivery Process</div>
            <h2 className="hpe-title">How ALG Data Guard Delivers HPE Infrastructure</h2>
            <p className="hpe-sub">Structured, engineer-led delivery from design through ongoing managed operations.</p>
          </div>
          <div className="hpe-lifecycle__steps">
            {lifecycleSteps.map(step => (
              <div key={step.title} className="hpe-lifecycle__step">
                <div className="hpe-lifecycle__num">{step.icon}</div>
                <h3 className="hpe-lifecycle__step-title">{step.title}</h3>
                <p className="hpe-lifecycle__step-desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Metrics ── */}
      <section className="hpe-metrics" ref={metricsRef}>
        <div className="hpe-shell">
          <div className="hpe-metrics__inner">
            <div><div className="hpe-metric__val">{c1}+</div><div className="hpe-metric__label">Countries with HPE Presence</div></div>
            <div><div className="hpe-metric__val">{c2}%</div><div className="hpe-metric__label">Data Availability Guarantee</div></div>
            <div><div className="hpe-metric__val">Top{c3}%</div><div className="hpe-metric__label">HPE Supercomputer Deployments</div></div>
            <div><div className="hpe-metric__val">{c4}+</div><div className="hpe-metric__label">HPE Systems Managed by ALG</div></div>
          </div>
        </div>
      </section>

      {/* ── Certifications ── */}
      <section className="hpe-certs">
        <div className="hpe-shell">
          <div className="hpe-certs__head">
            <div className="hpe-tag hpe-tag--green"><span className="hpe-blink" /> Partner Credentials</div>
            <h2 className="hpe-title">Certified HPE Gold Partner</h2>
            <p className="hpe-sub" style={{ margin:"0 auto", maxWidth:"580px" }}>ALG Data Guard's HPE Gold status and product certifications validate our depth of expertise across the HPE infrastructure portfolio.</p>
          </div>
          <div className="hpe-certs__grid">
            {certs.map(c => (
              <div key={c.name} className="hpe-cert-card">
                <span className="hpe-cert-card__icon">{c.icon}</span>
                <div className="hpe-cert-card__name">{c.name}</div>
                <p className="hpe-cert-card__desc">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="hpe-faq">
        <div className="hpe-shell">
          <div className="hpe-faq__head">
            <div className="hpe-tag hpe-tag--dark" style={{ margin:"0 auto 1rem" }}><span className="hpe-blink" /> FAQs</div>
            <h2 className="hpe-title">HPE Infrastructure Questions, Answered</h2>
            <p className="hpe-sub">Expert technical answers about HPE platforms and ALG Data Guard's delivery services.</p>
          </div>
          <div className="hpe-faq__list">
            {faqData.map((faq, i) => (
              <div key={i} className={`hpe-faq-item${openFaq === i ? " open" : ""}`}>
                <button className="hpe-faq-item__trigger" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                  <span className="hpe-faq-item__q">{faq.q}</span>
                  <span className="hpe-faq-item__arrow"><ChevronDown /></span>
                </button>
                <div className="hpe-faq-item__answer"><div className="hpe-faq-item__answer-inner">{faq.a}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="hpe-cta">
        <div className="hpe-cta__inner">
          <div className="hpe-tag hpe-tag--dark" style={{ margin:"0 auto 1.2rem" }}><span className="hpe-blink" /> Get Started</div>
          <h2 className="hpe-cta__title">Build Enterprise-Grade Infrastructure That Scales</h2>
          <p className="hpe-cta__sub">
            Whether you need to refresh aging servers, modernize to GreenLake as-a-service, deploy AI infrastructure, or extend computing to the edge — ALG Data Guard's HPE Gold-certified engineers are ready to design and deliver the right solution.
          </p>
          <div className="hpe-cta__actions">
            <button className="hpe-btn hpe-btn--green" onClick={() => navigate("/contact")}>
              Request an HPE Infrastructure Assessment <ArrowIcon />
            </button>
            <button className="hpe-btn hpe-btn--ghost-w" onClick={() => navigate("/contact")}>
              Get an HPE Quote
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}
